import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import cx from 'classnames';
import ARQuickLookModel from '../../../models/ar-quick-look';
import styles from './ar-quick-look.module.scss';
import getBreakpointFlags from '../../../helpers/get-breakpoint-flags';
import useWindowSize from '../../../hooks/useWindowSize';

type Props = {
  data: ARQuickLookModel;
  customClass?: string;
};

const ARQuickLook = ({ data, customClass }: Props) => {
  const {
    linkText,
    arModel,
    notAvailableText,
    quickLookIcon,
    placeholderImage,
    analyticsAttributes,
    hideMobileNotSupported
  } = data;
  const [isArEnabled, setIsArEnabled] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const { width } = useWindowSize();
  const { isMobile } = getBreakpointFlags(width);

  useEffect(() => {
    const a = document.createElement('a');

    if (a.relList.supports('ar')) {
      setIsArEnabled(true);
    }
  }, []);

  const handleClick = () => {
    linkRef?.current?.click();
  };

  if (!isArEnabled && isMobile && hideMobileNotSupported) {
    return null;
  }

  return (
    <div
      className={cx(styles['ar-quick-look'], customClass)}
      {...(isArEnabled && { onClick: handleClick })}
    >
      {quickLookIcon && (
        <div className={styles['ar-quick-look__icon-container']}>
          <Image
            alt={quickLookIcon.alt}
            src={quickLookIcon.src}
            layout="fill"
          />
        </div>
      )}
      {isArEnabled && (
        <a
          className={styles['ar-quick-look__text']}
          rel="ar"
          href={arModel?.src}
          ref={linkRef}
          {...(analyticsAttributes || {})}
        >
          {/* Safari needs first child to be an image to skip preview on click */}
          {placeholderImage && (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              alt={placeholderImage.alt}
              className={styles['ar-quick-look__image']}
              src={placeholderImage.src}
            />
          )}
          {linkText}
        </a>
      )}
      {!isArEnabled && (
        <span className={styles['ar-quick-look__text']}>
          {notAvailableText}
        </span>
      )}
    </div>
  );
};

export default ARQuickLook;
